import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAlertsStore } from './state/AlertsContainer';
import { useOrganizationStore } from './state/OrganizationContainer';
import useResolveCurrentOrg from './useResolveCurrentOrg';

export default function useCurrentOrgProjectsFetching() {
    const { t } = useTranslation();
    const { organizationId } = useResolveCurrentOrg();
    const { clientId, deviceId } = useParams();

    const orgAuthClients = useOrganizationStore((state) => state.orgAuthClients);
    const orgDeviceTypes = useOrganizationStore((state) => state.orgDeviceTypes);
    const isLoading = useOrganizationStore((state) => state.loadingData.projects);
    const { getOrgAuthClients, getOrgDeviceTypes } = useOrganizationStore.getState();
    const [error, setError] = useState({ client: false, device: false });

    const fetchOrgData = useCallback(async () => {
        const { currentOrgId, loadingData } = useOrganizationStore.getState();
        if (!organizationId || currentOrgId == organizationId || loadingData.projects) {
            return;
        }

        try {
            useOrganizationStore.getState().handleLoadingDataChange('projects', true);
            await Promise.all([getOrgAuthClients(organizationId), getOrgDeviceTypes(organizationId)]);
            useOrganizationStore.setState({ currentOrgId: organizationId });
            setError({ client: false, device: false });
        } catch (error) {
            useAlertsStore.getState().showError(t('common:organization.projects.error'));
            setError({ client: true, device: true });
        } finally {
            useOrganizationStore.getState().handleLoadingDataChange('projects', false);
        }
    }, [organizationId]);

    useEffect(() => {
        fetchOrgData();
    }, [fetchOrgData]);

    const currentOrgClient = useMemo(() => {
        if (!orgAuthClients || !clientId) return null;

        const client = orgAuthClients?.find((client) => client.id === clientId) ?? null;
        if (!client && error.client === false) {
            setError((prev) => ({ ...prev, client: true }));
        } else if (error.client === true) {
            setError((prev) => ({ ...prev, client: false }));
        }
        return client;
    }, [orgAuthClients, clientId]);

    const currentOrgDeviceType = useMemo(() => {
        if (!orgDeviceTypes || !deviceId) return null;

        const device = orgDeviceTypes.find((device) => device.id === deviceId) ?? null;
        if (!device && error.device === false) {
            setError((prev) => ({ ...prev, device: true }));
        } else if (error.device === true) {
            setError((prev) => ({ ...prev, device: false }));
        }
        return device;
    }, [orgDeviceTypes, deviceId]);

    return {
        error,
        currentOrgClient,
        currentOrgDeviceType,
        authClients: orgAuthClients,
        deviceTypes: orgDeviceTypes,
        isLoading,
    };
}
